import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, arrayUnion, collection, doc, deleteDoc, getDocs, onSnapshot, query, updateDoc, where, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getBlob, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../lib/firebase';
import { BankAccount, BankStatement, BankStatementRow, Debt, Transaction } from '../types';
import { UploadCloud, FileText, FileSpreadsheet, Trash2, Clock, Link2, ArrowLeftRight, X, ChevronDown, Landmark } from 'lucide-react';
import { format } from 'date-fns';
import { parseCsvText, inferColumnMapping, isMappingComplete, mapToParsedRows, buildFingerprint, classifyRowStatus } from '../lib/csvStatementParser';
import { findTransferMatches } from '../lib/transferMatching';
import { findDebtPaymentMatches } from '../lib/debtPaymentMatching';
import StatementReviewTable from './StatementReviewTable';

interface ReconcileDashboardProps {
  householdId: string;
  userId: string;
  bankAccounts: BankAccount[];
  debts: Debt[];
  transactions: Transaction[];
  bankStatements: BankStatement[];
}

const STATUS_LABELS: Record<BankStatement['status'], string> = {
  pending_match: 'Needs account match',
  matched: 'Matching…',
  parsing: 'Parsing…',
  parsed: 'Ready to review',
  parse_error: 'Parse failed',
  needs_mapping: 'Needs column mapping',
  reviewed: 'Reviewed',
  imported: 'Imported',
};

const STATUS_STYLES: Record<BankStatement['status'], string> = {
  pending_match: 'bg-amber-100 text-amber-700',
  matched: 'bg-blue-100 text-blue-700',
  parsing: 'bg-blue-100 text-blue-700',
  parsed: 'bg-indigo-100 text-indigo-700',
  parse_error: 'bg-rose-100 text-rose-700',
  needs_mapping: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-slate-200 text-slate-700',
  imported: 'bg-emerald-100 text-emerald-700',
};

export interface AccountOption {
  key: string; // `${accountType}:${accountId}`
  accountType: 'bank_account' | 'debt';
  accountId: string;
  name: string;
  last4?: string;
  matchAliases?: string[];
  csvColumnMapping?: Record<string, string>;
}

/** Normalizes a filename into a reusable alias -- strips extension,
 *  digits (dates/timestamps vary upload to upload), and separators. */
function deriveAliasFromFilename(fileName: string): string {
  return fileName
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/[0-9]/g, ' ')
    .replace(/[^a-z]+/g, ' ')
    .trim();
}

export default function ReconcileDashboard({ householdId, userId, bankAccounts, debts, transactions, bankStatements }: ReconcileDashboardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [matchingStatementId, setMatchingStatementId] = useState<string | null>(null);
  const [selectedAccountKey, setSelectedAccountKey] = useState<string>('');
  const [rowsByStatement, setRowsByStatement] = useState<Record<string, BankStatementRow[]>>({});
  const [transferCandidates, setTransferCandidates] = useState<BankStatementRow[]>([]);
  const [manualMapping, setManualMapping] = useState<Record<string, { date: string; description: string; amount: string; debit: string; credit: string; dateFormat: string; }>>({});
  const subscribedStatementIds = useRef<Set<string>>(new Set());
  const processingStatementIds = useRef<Set<string>>(new Set());
  const scannedForTransfersStatementIds = useRef<Set<string>>(new Set());
  // Statements are grouped into per-bank "drawers" and start collapsed --
  // an empty set means nothing is expanded yet, so a household with several
  // linked institutions doesn't open to a long undifferentiated list.
  const [expandedBankGroups, setExpandedBankGroups] = useState<Set<string>>(new Set());

  const accountOptions: AccountOption[] = [
    ...bankAccounts.map(a => ({ key: `bank_account:${a.id}`, accountType: 'bank_account' as const, accountId: a.id!, name: a.name, last4: a.last4, matchAliases: a.matchAliases, csvColumnMapping: a.csvColumnMapping })),
    ...debts.filter(d => d.type === 'credit_card' || d.type === 'line_of_credit').map(d => ({ key: `debt:${d.id}`, accountType: 'debt' as const, accountId: d.id!, name: `${d.name} (${d.type === 'credit_card' ? 'Credit Card' : 'Line of Credit'})`, last4: d.last4, matchAliases: d.matchAliases, csvColumnMapping: d.csvColumnMapping })),
  ];

  const findAccount = (accountType: string, accountId: string): AccountOption | undefined =>
    accountOptions.find(a => a.accountType === accountType && a.accountId === accountId);

  const suggestAccountKey = (fileName: string): string | undefined => {
    const lower = fileName.toLowerCase();
    const byLast4 = accountOptions.find(a => a.last4 && lower.includes(a.last4.toLowerCase()));
    if (byLast4) return byLast4.key;
    const byAlias = accountOptions.find(a => a.matchAliases?.some(alias => alias && lower.includes(alias)));
    return byAlias?.key;
  };

  // Each Plaid sync's bank_statement is already scoped to a single
  // institution (plaidSyncAllTransactions names it "Plaid Sync - <bank>"),
  // so that's a reliable label without needing this statement's rows. A
  // CSV/PDF statement groups under whichever account it's matched to; one
  // not yet matched has no bank to group under, so it gets its own drawer.
  const bankGroupLabel = (statement: BankStatement): string => {
    if (statement.fileType === 'plaid_sync') {
      return statement.fileName.replace(/^Plaid Sync - /, '');
    }
    const acc = statement.accountId && statement.accountType ? findAccount(statement.accountType, statement.accountId) : undefined;
    return acc?.name || 'Unmatched statements';
  };

  // Statements needing a look (not just sitting imported/reviewed) surface
  // as a count on the collapsed drawer header, so there's a reason to open
  // one without expanding every drawer first.
  const NEEDS_ATTENTION_STATUSES = new Set<BankStatement['status']>(['pending_match', 'needs_mapping', 'parse_error', 'parsed']);

  const bankGroups = useMemo(() => {
    const groups = new Map<string, BankStatement[]>();
    for (const statement of bankStatements) {
      const label = bankGroupLabel(statement);
      const list = groups.get(label) || [];
      list.push(statement);
      groups.set(label, list);
    }
    // Most recently active bank first -- bankStatements itself already
    // arrives newest-first, so each group's own first entry is its latest.
    return Array.from(groups.entries()).sort((a, b) => {
      const aTime = a[1][0]?.createdAt?.getTime() || 0;
      const bTime = b[1][0]?.createdAt?.getTime() || 0;
      return bTime - aTime;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankStatements, bankAccounts, debts]);

  const toggleBankGroup = (label: string) => {
    setExpandedBankGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  // Subscribe to rows for every statement that has something to review or
  // show, scoped per-statement (never the whole bank_statement_rows
  // collection at once).
  useEffect(() => {
    const toWatch = bankStatements.filter(s => ['parsed', 'reviewed', 'imported'].includes(s.status) && s.id && !subscribedStatementIds.current.has(s.id));
    toWatch.forEach(s => {
      subscribedStatementIds.current.add(s.id!);
      onSnapshot(
        // Firestore security rules check resource.data.householdId per doc,
        // but for a *list/query* request the rule can only allow it if the
        // query itself is providably scoped on that same field -- filtering
        // by statementId alone (even though every real row for it already
        // has the right householdId) isn't enough and gets denied outright.
        // Found live: this returned a real PERMISSION_DENIED, meaning the
        // review table never showed a single row until this was added.
        query(collection(db, 'bank_statement_rows'), where('householdId', '==', householdId), where('statementId', '==', s.id)),
        (snapshot) => {
          const rows = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            date: d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date),
            createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(),
          })) as BankStatementRow[];
          rows.sort((a, b) => b.date.getTime() - a.date.getTime());
          setRowsByStatement(prev => ({ ...prev, [s.id!]: rows }));
        },
        (error) => console.error('Error loading statement rows:', error)
      );
    });
  }, [bankStatements]);

  // A single household-wide subscription to matched transfer pairs
  // (bounded: only rows already flagged transfer_candidate, never the
  // whole bank_statement_rows collection) -- these span two different
  // statements/accounts by definition, so they can't live in the
  // per-statement subscription above.
  useEffect(() => {
    if (!householdId) return;
    const unsub = onSnapshot(
      query(collection(db, 'bank_statement_rows'), where('householdId', '==', householdId), where('status', '==', 'transfer_candidate')),
      (snapshot) => {
        const rows = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          date: d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date),
          createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(),
        })) as BankStatementRow[];
        setTransferCandidates(rows);
      },
      (error) => console.error('Error loading transfer candidates:', error)
    );
    return () => unsub();
  }, [householdId]);

  // Once a statement finishes parsing, re-scan the whole household's
  // still-pending rows for transfer matches -- a transfer's two legs
  // almost always live in two different statements/accounts, so this has
  // to look beyond just the statement that just finished.
  useEffect(() => {
    const newlyParsed = bankStatements.filter(s => s.status === 'parsed' && s.id && !scannedForTransfersStatementIds.current.has(s.id));
    if (newlyParsed.length === 0) return;
    newlyParsed.forEach(s => scannedForTransfersStatementIds.current.add(s.id!));
    scanForTransfers();
    scanForDebtPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankStatements]);

  const scanForTransfers = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'bank_statement_rows'),
        where('householdId', '==', householdId),
        where('status', 'in', ['new', 'possible_duplicate'])
      ));
      const rows = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        date: d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date),
        createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(),
      })) as BankStatementRow[];

      const matches = findTransferMatches(rows, debts);
      if (matches.length === 0) return;

      const batch = writeBatch(db);
      for (const m of matches) {
        const groupId = doc(collection(db, 'bank_statement_rows')).id;
        batch.update(doc(db, 'bank_statement_rows', m.debitRowId), { status: 'transfer_candidate', transferGroupId: groupId });
        batch.update(doc(db, 'bank_statement_rows', m.creditRowId), { status: 'transfer_candidate', transferGroupId: groupId });
      }
      await batch.commit();
    } catch (err) {
      console.error('Error scanning for transfers:', err);
    }
  };

  // Same household-wide re-scan, for debts with a paymentMatchHint set --
  // see findDebtPaymentMatches for why this is a separate pass from
  // transfer-matching above (a transfer needs both legs to be tracked
  // accounts; a mortgage/etc lender never is).
  const scanForDebtPayments = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'bank_statement_rows'),
        where('householdId', '==', householdId),
        where('status', 'in', ['new', 'possible_duplicate'])
      ));
      const rows = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        date: d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date),
        createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(),
      })) as BankStatementRow[];

      const matches = findDebtPaymentMatches(rows, debts);
      if (matches.length === 0) return;

      const batch = writeBatch(db);
      for (const m of matches) {
        batch.update(doc(db, 'bank_statement_rows', m.rowId), { suggestedDebtId: m.debtId });
      }
      await batch.commit();
    } catch (err) {
      console.error('Error scanning for debt payment matches:', err);
    }
  };

  const handleConfirmTransfer = async (debitRowId: string, creditRowId: string) => {
    try {
      const commit = httpsCallable(functions, 'commitTransferPair');
      await commit({ debitRowId, creditRowId });
    } catch (err) {
      console.error('Error confirming transfer:', err);
      alert('Failed to confirm this transfer. Please try again.');
    }
  };

  const handleUnpairTransfer = async (debitRowId: string, creditRowId: string) => {
    try {
      await Promise.all([
        updateDoc(doc(db, 'bank_statement_rows', debitRowId), { status: 'new', transferGroupId: null }),
        updateDoc(doc(db, 'bank_statement_rows', creditRowId), { status: 'new', transferGroupId: null }),
      ]);
    } catch (err) {
      console.error('Error unpairing transfer:', err);
    }
  };

  // Once a statement is matched to an account, parse it automatically --
  // CSV parses client-side, PDF hands off to the parseStatementPdf Cloud
  // Function (needs Gemini + Admin Storage access, neither available in
  // the browser).
  useEffect(() => {
    const toParse = bankStatements.filter(s => s.status === 'matched' && s.id && !processingStatementIds.current.has(s.id));
    toParse.forEach(s => {
      processingStatementIds.current.add(s.id!);
      if (s.fileType === 'csv') parseCsvStatement(s);
      else parsePdfStatement(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankStatements]);

  const parsePdfStatement = async (statement: BankStatement) => {
    try {
      const parse = httpsCallable(functions, 'parseStatementPdf');
      await parse({ statementId: statement.id });
    } catch (err) {
      console.error('Error parsing PDF statement:', err);
      await updateDoc(doc(db, 'bank_statements', statement.id!), {
        status: 'parse_error',
        parseError: 'Something went wrong reading this PDF.',
      }).catch(() => {});
    }
  };

  const parseCsvStatement = async (statement: BankStatement) => {
    const statementRef = doc(db, 'bank_statements', statement.id!);
    try {
      await updateDoc(statementRef, { status: 'parsing' });

      const account = findAccount(statement.accountType!, statement.accountId!);
      // A plain fetch(fileUrl) against the public download URL is blocked
      // by CORS from a real browser (confirmed live -- Node's fetch used
      // during earlier server-side verification doesn't enforce CORS at
      // all, which is exactly why this never showed up until an actual
      // browser drove it). getBlob() goes through the Storage SDK's own
      // properly-configured request path instead.
      const blob = await getBlob(ref(storage, statement.storagePath));
      const text = await blob.text();
      const { headers, rows: rawRows } = parseCsvText(text);

      const hasSavedMapping = account?.csvColumnMapping && Object.keys(account.csvColumnMapping).length > 0;
      const mapping = hasSavedMapping
        ? account.csvColumnMapping!
        : inferColumnMapping(headers);

      if (!hasSavedMapping || !isMappingComplete(mapping)) {
        await updateDoc(statementRef, {
          status: 'needs_mapping',
          rawHeaders: headers,
          inferredMapping: mapping,
        });
        return;
      }

      const parsedRows = mapToParsedRows(rawRows, mapping);
      if (parsedRows.length === 0) {
        await updateDoc(statementRef, { status: 'parse_error', parseError: 'No valid transaction rows found in this file.' });
        return;
      }

      const batch = writeBatch(db);
      let minDate = parsedRows[0].date;
      let maxDate = parsedRows[0].date;

      for (const row of parsedRows) {
        if (row.date < minDate) minDate = row.date;
        if (row.date > maxDate) maxDate = row.date;
        const classifyResult = classifyRowStatus(row, transactions, statement.accountId!, statement.accountType!);
        const rowRef = doc(collection(db, 'bank_statement_rows'));
        batch.set(rowRef, {
          householdId,
          statementId: statement.id,
          accountId: statement.accountId,
          accountType: statement.accountType,
          date: row.date,
          description: row.description,
          amount: row.amount,
          direction: row.direction,
          fingerprint: buildFingerprint(row),
          status: classifyResult.status,
          ...(classifyResult.duplicateOfTxIds?.length ? { duplicateOfTxIds: classifyResult.duplicateOfTxIds } : {}),
          createdAt: new Date(),
        });
      }

      // Save the confirmed mapping onto the account so future statements
      // for it skip straight to parsing.
      if (!account?.csvColumnMapping || Object.keys(account.csvColumnMapping).length === 0) {
        const collectionName = statement.accountType === 'bank_account' ? 'bank_accounts' : 'debts';
        batch.update(doc(db, collectionName, statement.accountId!), { csvColumnMapping: mapping });
      }

      batch.update(statementRef, {
        status: 'parsed',
        statementPeriodStart: minDate,
        statementPeriodEnd: maxDate,
      });

      await batch.commit();
    } catch (err) {
      console.error('Error parsing statement:', err);
      await updateDoc(statementRef, { status: 'parse_error', parseError: 'Something went wrong reading this file.' }).catch(() => {});
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const fileType: BankStatement['fileType'] | null = lowerName.endsWith('.csv')
      ? 'csv'
      : lowerName.endsWith('.pdf')
        ? 'pdf'
        : null;

    if (!fileType) {
      setUploadError('Only CSV or PDF statements are supported right now.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const storagePath = `households/${householdId}/statements/${Date.now()}_${file.name}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, file);
      const fileUrl = await getDownloadURL(fileRef);

      const statementRef = await addDoc(collection(db, 'bank_statements'), {
        householdId,
        userId,
        fileName: file.name,
        fileUrl,
        storagePath,
        fileType,
        status: 'pending_match',
        createdAt: new Date(),
      });

      const suggested = suggestAccountKey(file.name);
      if (suggested) {
        setMatchingStatementId(statementRef.id);
        setSelectedAccountKey(suggested);
      }
    } catch (err) {
      console.error('Error uploading statement:', err);
      setUploadError('Failed to upload the statement. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleConfirmMatch = async (statement: BankStatement) => {
    if (!selectedAccountKey) return;
    const [accountType, accountId] = selectedAccountKey.split(':') as ['bank_account' | 'debt', string];
    const account = findAccount(accountType, accountId);

    await updateDoc(doc(db, 'bank_statements', statement.id!), {
      accountId,
      accountType,
      status: statement.fileType === 'pdf' ? 'matched' : 'matched',
    });

    // If this wasn't already a known match (no last4/alias hit), remember
    // a filename-derived alias so future uploads of this account
    // auto-suggest instead of requiring another manual pick.
    const lowerName = statement.fileName.toLowerCase();
    const alreadyKnown = (account?.last4 && lowerName.includes(account.last4.toLowerCase()))
      || account?.matchAliases?.some(alias => alias && lowerName.includes(alias));
    if (!alreadyKnown) {
      const alias = deriveAliasFromFilename(statement.fileName);
      if (alias.length >= 3) {
        const collectionName = accountType === 'bank_account' ? 'bank_accounts' : 'debts';
        await updateDoc(doc(db, collectionName, accountId), { matchAliases: arrayUnion(alias) });
      }
    }

    setMatchingStatementId(null);
    setSelectedAccountKey('');
  };

  const handleDelete = async (statement: BankStatement) => {
    if (!statement.id) return;
    if (!window.confirm(`Delete "${statement.fileName}"? This can't be undone.`)) return;
    try {
      const rowsSnap = await getDocs(query(collection(db, 'bank_statement_rows'), where('householdId', '==', householdId), where('statementId', '==', statement.id)));
      await Promise.all(rowsSnap.docs.map(d => deleteDoc(d.ref)));
      await deleteDoc(doc(db, 'bank_statements', statement.id));
      if (statement.storagePath) {
        try {
          await deleteObject(ref(storage, statement.storagePath));
        } catch (storageErr) {
          console.warn('Failed to delete storage object, ignoring:', storageErr);
        }
      }
    } catch (err) {
      console.error('Error deleting statement:', err);
      alert('Failed to delete the statement.');
    }
  };

  const handleApproveImport = async (statement: BankStatement, selections: { rowId: string; include: boolean; category: string; description: string; confirmDebtId?: string }[]) => {
    try {
      const commit = httpsCallable(functions, 'commitStatementImport');
      await commit({ statementId: statement.id, rows: selections });
    } catch (err) {
      console.error('Error importing statement:', err);
      alert('Failed to import. Please try again.');
    }
  };

  const handleConfirmManualMapping = async (statement: BankStatement) => {
    if (!statement.id) return;
    const selection = manualMapping[statement.id] || statement.inferredMapping || {};
    if (!selection.date || !selection.description || (!selection.amount && !selection.debit && !selection.credit)) {
      alert("Please select Date, Description, and either Amount or Debit/Credit columns.");
      return;
    }
    const mapping: Record<string, string> = {
      date: selection.date,
      description: selection.description,
    };
    if (selection.amount) mapping.amount = selection.amount;
    if (selection.debit) mapping.debit = selection.debit;
    if (selection.credit) mapping.credit = selection.credit;
    if (selection.dateFormat) mapping.dateFormat = selection.dateFormat;

    if (statement.accountType && statement.accountId) {
      const collectionName = statement.accountType === 'bank_account' ? 'bank_accounts' : 'debts';
      await updateDoc(doc(db, collectionName, statement.accountId), { csvColumnMapping: mapping });
    }

    await updateDoc(doc(db, 'bank_statements', statement.id), {
      status: 'matched' // sending it back to matched will re-trigger parseCsvStatement
    });
    
    // Clear processing so it will actually run again
    processingStatementIds.current.delete(statement.id);
  };

  const statementBearingAccounts = accountOptions.length > 0;

  const transferPairs = (() => {
    const byGroup = new Map<string, BankStatementRow[]>();
    for (const row of transferCandidates) {
      if (!row.transferGroupId) continue;
      const list = byGroup.get(row.transferGroupId) || [];
      list.push(row);
      byGroup.set(row.transferGroupId, list);
    }
    return Array.from(byGroup.values())
      .filter(pair => pair.length === 2)
      .map(pair => {
        const debitRow = pair.find(r => r.direction === 'debit')!;
        const creditRow = pair.find(r => r.direction === 'credit')!;
        return { debitRow, creditRow };
      })
      .filter(p => p.debitRow && p.creditRow);
  })();

  return (
    <div className="flex flex-col gap-6">
      {transferPairs.length > 0 && (
        <div className="bg-white rounded-xl border border-purple-200 p-6">
          <div className="flex items-center gap-2 mb-1">
            <ArrowLeftRight className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-slate-900">Matched Transfers</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            These look like the same real-world transfer showing up on two different accounts.
            Confirm to import them as one Transfer, or unpair to import each side separately.
          </p>
          <div className="flex flex-col gap-3">
            {transferPairs.map(({ debitRow, creditRow }) => {
              const fromAccount = findAccount(debitRow.accountType, debitRow.accountId);
              const toAccount = findAccount(creditRow.accountType, creditRow.accountId);
              return (
                <div key={debitRow.transferGroupId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{fromAccount?.name || 'Unknown account'}</p>
                      <p className="text-xs text-slate-500">{debitRow.description} — {format(debitRow.date, 'MMM d, yyyy')}</p>
                    </div>
                    <ArrowLeftRight className="w-4 h-4 text-purple-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{toAccount?.name || 'Unknown account'}</p>
                      <p className="text-xs text-slate-500">{creditRow.description} — {format(creditRow.date, 'MMM d, yyyy')}</p>
                    </div>
                    <p className="font-semibold text-slate-900 shrink-0">${debitRow.amount.toFixed(2)}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleConfirmTransfer(debitRow.id!, creditRow.id!)}
                      className="text-xs font-semibold bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700"
                    >
                      Confirm Transfer
                    </button>
                    <button
                      onClick={() => handleUnpairTransfer(debitRow.id!, creditRow.id!)}
                      className="text-xs font-semibold text-slate-500 px-3 py-1.5 rounded-md hover:bg-slate-200 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Unpair
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Upload a statement</h2>
            <p className="text-sm text-slate-500 mt-1">
              CSV or PDF, from any bank account, credit card, or line of credit. You'll match it
              to the right account and review every transaction before anything is imported.
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !statementBearingAccounts}
            className="shrink-0 flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <UploadCloud className="w-4 h-4" />
            {uploading ? 'Uploading…' : 'Upload Statement'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.pdf,text/csv,application/pdf"
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>
        {!statementBearingAccounts && (
          <p className="text-sm text-amber-600 mt-3">
            Add a bank account, credit card, or line of credit first — there's nothing yet to
            reconcile a statement against.
          </p>
        )}
        {uploadError && <p className="text-sm text-rose-600 mt-3">{uploadError}</p>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Statements</h2>
        {bankStatements.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No statements uploaded yet.</p>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-slate-100">
            {bankGroups.map(([label, statements]) => {
              const isExpanded = expandedBankGroups.has(label);
              const needsAttentionCount = statements.filter(s => NEEDS_ATTENTION_STATUSES.has(s.status)).length;
              return (
                <div key={label} className="py-2">
                  <button
                    type="button"
                    onClick={() => toggleBankGroup(label)}
                    className="w-full flex items-center justify-between gap-3 py-2 px-1 rounded-lg hover:bg-slate-50 transition-colors text-left"
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      <Landmark className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="font-semibold text-slate-800 truncate">{label}</span>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {statements.length} statement{statements.length === 1 ? '' : 's'}
                      </span>
                    </span>
                    {needsAttentionCount > 0 && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
                        {needsAttentionCount} to review
                      </span>
                    )}
                  </button>
                  {isExpanded && (
                    <div className="flex flex-col divide-y divide-slate-100 pl-1">
                      {statements.map(statement => {
              const matchedAccount = statement.accountId && statement.accountType
                ? findAccount(statement.accountType, statement.accountId)
                : undefined;
              return (
                <div key={statement.id} className="py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {statement.fileType === 'csv' ? (
                        <FileSpreadsheet className="w-5 h-5 text-slate-400 shrink-0" />
                      ) : (
                        <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{statement.fileName}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {format(statement.createdAt, 'MMM d, yyyy h:mm a')}
                          {matchedAccount && <><Link2 className="w-3 h-3 ml-1" /> {matchedAccount.name}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLES[statement.status]}`}>
                        {STATUS_LABELS[statement.status]}
                      </span>
                      {statement.status === 'pending_match' && (
                        <button
                          onClick={() => { setMatchingStatementId(statement.id!); setSelectedAccountKey(suggestAccountKey(statement.fileName) || ''); }}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Match account
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(statement)}
                        className="text-slate-300 hover:text-rose-500 transition-colors"
                        title="Delete statement"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {matchingStatementId === statement.id && (
                    <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-slate-50 rounded-lg p-3">
                      <select
                        value={selectedAccountKey}
                        onChange={(e) => setSelectedAccountKey(e.target.value)}
                        className="flex-1 w-full sm:w-auto px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Which account is this statement for?</option>
                        {accountOptions.map(a => (
                          <option key={a.key} value={a.key}>{a.name}</option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleConfirmMatch(statement)}
                          disabled={!selectedAccountKey}
                          className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => { setMatchingStatementId(null); setSelectedAccountKey(''); }}
                          className="text-xs font-semibold text-slate-500 px-3 py-1.5 rounded-md hover:bg-slate-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {statement.status === 'needs_mapping' && statement.rawHeaders && (
                    <div className="mt-3 bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium mb-2">Please confirm column mapping.</p>
                      <p className="text-xs text-amber-700 mb-4">Match the columns from your file to the required fields. You can map a single Amount column, or split Debit and Credit. This will be saved for future uploads!</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {['date', 'description', 'amount', 'debit', 'credit'].map(field => {
                          const currentVal = manualMapping[statement.id!]?.[field as keyof typeof manualMapping[string]] ?? statement.inferredMapping?.[field] ?? '';
                          return (
                            <div key={field}>
                              <label className="block text-xs font-semibold text-amber-900 capitalize mb-1">{field} Column</label>
                              <select
                                value={currentVal}
                                onChange={(e) => setManualMapping(prev => ({
                                  ...prev,
                                  [statement.id!]: { ...(prev[statement.id!] || statement.inferredMapping || {}), [field]: e.target.value } as any
                                }))}
                                className="w-full px-3 py-1.5 bg-white border border-amber-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                              >
                                <option value="">Select column...</option>
                                {statement.rawHeaders!.map(h => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4">
                        <label className="block text-xs font-semibold text-amber-900 capitalize mb-1">Date Format (Optional Override)</label>
                        <select
                          value={manualMapping[statement.id!]?.dateFormat ?? statement.inferredMapping?.dateFormat ?? ''}
                          onChange={(e) => setManualMapping(prev => ({
                            ...prev,
                            [statement.id!]: { ...(prev[statement.id!] || statement.inferredMapping || {}), dateFormat: e.target.value } as any
                          }))}
                          className="w-full max-w-xs px-3 py-1.5 bg-white border border-amber-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Auto-detect (MM/DD/YYYY default)</option>
                          <option value="MM/DD/YYYY">MM/DD/YYYY (US format)</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY (International format)</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD (ISO format)</option>
                        </select>
                      </div>
                      <button
                        onClick={() => handleConfirmManualMapping(statement)}
                        className="mt-4 text-xs font-semibold bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700"
                      >
                        Confirm Mapping & Parse
                      </button>
                    </div>
                  )}

                  {statement.status === 'parse_error' && statement.parseError && (
                    <p className="text-xs text-rose-600 mt-2">{statement.parseError}</p>
                  )}

                  {['parsed', 'reviewed', 'imported'].includes(statement.status) && (matchedAccount || statement.fileType === 'plaid_sync') && (
                    <div className="mt-2">
                      {statement.status === 'parsed' && statement.fileType !== 'plaid_sync' && matchedAccount && (
                        <div className="flex items-center gap-3 mb-3 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                          <label className="text-xs font-semibold text-slate-700">Dates look wrong?</label>
                          <select
                            className="text-xs font-medium border border-slate-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={matchedAccount.csvColumnMapping?.dateFormat || ''}
                            onChange={async (e) => {
                              const newFormat = e.target.value;
                              const collectionName = statement.accountType === 'bank_account' ? 'bank_accounts' : 'debts';
                              const newMapping = { ...(matchedAccount.csvColumnMapping || {}) };
                              if (newFormat) {
                                newMapping.dateFormat = newFormat;
                              } else {
                                delete newMapping.dateFormat;
                              }
                              await updateDoc(doc(db, collectionName, statement.accountId!), { csvColumnMapping: newMapping });
                              
                              // Clear the existing parsed rows since they have wrong dates
                              const qRows = query(collection(db, 'bank_statement_rows'), where('statementId', '==', statement.id!));
                              const snap = await getDocs(qRows);
                              const batch = writeBatch(db);
                              snap.docs.forEach(d => batch.delete(d.ref));
                              await batch.commit();

                              // Re-trigger parse
                              processingStatementIds.current.delete(statement.id!);
                              await updateDoc(doc(db, 'bank_statements', statement.id!), { status: 'matched' });
                            }}
                          >
                            <option value="">Change date format...</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY (US format)</option>
                            <option value="DD/MM/YYYY">DD/MM/YYYY (International format)</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD (ISO format)</option>
                          </select>
                        </div>
                      )}
                      <StatementReviewTable
                        statement={statement}
                        accountName={matchedAccount?.name}
                        accountOptions={accountOptions}
                        rows={rowsByStatement[statement.id!] || []}
                        transactions={transactions}
                        debts={debts}
                        onApprove={(selections) => handleApproveImport(statement, selections)}
                      />
                    </div>
                  )}
                </div>
              );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
